import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { LabResult } from '@/components/LabResultsTable';
import ExplainButton from './ExplainButton';
import Sparkline from './Sparkline';
import { groupLabsByPanel, PANEL_LABEL_KEYS, type LabSeries } from './lab-panels';
import { formatRecordDateTime, slugify } from './records-utils';

interface LabPanelsProps {
  labResults: LabResult[];
  /** Plain-language names from the startup payload, matched by test name. */
  tests?: Array<{ name: string; plainName: string }>;
  statusLabel: (status: string) => string;
  onAskQuestion: (question: string) => void;
  variant: 'main' | 'rail';
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
  normal: 'text-gray-600 bg-gray-100',
  low: 'text-amber-700 bg-amber-50',
  high: 'text-amber-700 bg-amber-50',
  critical: 'text-red-700 bg-red-50',
};

export default function LabPanels({ labResults, tests, statusLabel, onAskQuestion, variant }: LabPanelsProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || 'en';
  const [openKey, setOpenKey] = useState<string | null>(null);
  const rowPadding = variant === 'rail' ? 'px-3' : 'px-4';
  const plainNames = new Map((tests || []).map((x) => [x.name.trim().toLowerCase(), x.plainName]));
  const groups = groupLabsByPanel(labResults);

  const renderSeries = (series: LabSeries) => {
    const slug = slugify(series.key);
    const lab = series.latest;
    const plainName = plainNames.get(series.key);
    const showClinical = !!plainName && plainName.toLowerCase() !== series.name.toLowerCase();
    const status = (lab.status || '').toLowerCase();
    const canExpand = series.results.length > 1;
    const hasSparkline = series.numeric.length >= 2;
    const open = openKey === series.key;
    const result = `${lab.value}${lab.unit ? ` ${lab.unit}` : ''}`;
    return (
      <div key={series.key} data-testid={`lab-series-${slug}`}>
        <div className={`flex items-start gap-3 ${rowPadding} py-2.5`}>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-text-primary">
              <span>{plainName || series.name}</span>
              {showClinical && <span className="font-normal text-text-secondary"> ({series.name})</span>}
            </p>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_BADGE_CLASSES[status] || STATUS_BADGE_CLASSES.normal}`}>
                {statusLabel(lab.status)}
              </span>
              {lab.referenceRange && lab.referenceRange !== 'N/A' && (
                <span className="text-sm text-text-secondary">{t('records.usualRange', { range: lab.referenceRange })}</span>
              )}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-sm font-semibold text-text-primary leading-tight">
              <span>{String(lab.value)}</span>
              {lab.unit && <span className="font-normal text-text-secondary"> {lab.unit}</span>}
            </p>
            <p className="text-xs text-text-secondary mt-0.5">{formatRecordDateTime(lab.date, language)}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {canExpand ? (
              <button
                type="button"
                onClick={() => setOpenKey(open ? null : series.key)}
                aria-label={open ? t('records.hideTrend') : t('records.showTrend')}
                aria-expanded={open}
                className="flex items-center gap-1 text-text-secondary"
                data-testid={`button-trend-${slug}`}
              >
                {hasSparkline && <Sparkline values={series.numeric} testId={`sparkline-${slug}`} />}
                {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            ) : series.results.length === 1 ? (
              <span className="text-xs text-text-secondary">{t('records.drawnOnce')}</span>
            ) : null}
            <ExplainButton
              onClick={() => onAskQuestion(t('explain.labResult', { testName: series.name, result, status: statusLabel(lab.status) }))}
              ariaLabel={t('explain.labResultAriaLabel', { testName: series.name })}
              testId={`button-explain-test-${lab.id}`}
            />
          </div>
        </div>
        {canExpand && open && (
          <div className={`${rowPadding} pb-3`} data-testid={`trend-${slug}`}>
            <ul className="text-sm text-text-secondary space-y-0.5">
              {series.results.map((r) => (
                <li key={r.id} className="flex justify-between gap-3">
                  <span>{formatRecordDateTime(r.date, language)}</span>
                  <span className="font-medium text-text-primary">
                    <span>{String(r.value)}</span>
                    {r.unit && <span> {r.unit}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <section key={group.panel} data-testid={`lab-panel-${group.panel}`}>
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-sm font-semibold text-text-primary">{t(PANEL_LABEL_KEYS[group.panel] || 'records.panelOther')}</h3>
            <span className="text-xs text-text-secondary">{formatRecordDateTime(group.newestDate, language)}</span>
          </div>
          <div className="rounded-lg border bg-white divide-y">{group.tests.map(renderSeries)}</div>
        </section>
      ))}
    </div>
  );
}
